/**
 * Registration candidates beside a support request — Task 13.4, requirements 10.1, 11.6, and 15.1–15.5.
 *
 * ## A match is a candidate, and the page says so twice
 *
 * The lookup is an exact match on a normalised email address, which is a useful place to start and proof of
 * nothing: an address can be shared, reused, mistyped into someone else's account, or simply belong to a person
 * other than the one who filled in the form. So the panel states that in Spanish for the operator reading it,
 * and carries the contract's own `registrationIdentityNotice` verbatim underneath. Two sentences rather than
 * one because they do different jobs — the first is the instruction, the second is the API's own wording, which
 * is what a future reader will find in the payload.
 *
 * ## Zero, one, and several are three different answers
 *
 * None means the lookup found nothing, and that is not a fault: this repository's registrations table is
 * synthetic and may be empty, which is a legitimate no-match rather than a broken query. One is the ordinary
 * case. Several is the interesting one, and every candidate is shown — hiding the ambiguity behind a "best"
 * match is how the wrong person gets helped.
 *
 * ## Only the nine fields the support contract carries
 *
 * `RegistrationSupportSummary` is deliberately field-minimised: no birth date, no gender, no address, no Onfido
 * detail, no document identifiers, no KYC override, and no raw source record. Those columns are absent from the
 * operational projection too, so there is nothing here to leak — but the panel is written against the contract's
 * own field list, and the suite asserts the rendered set equals it, so a widened payload would not quietly
 * appear on screen.
 *
 * Nothing here edits, links, or lists registrations. There is no such endpoint and this task adds none.
 */
import {
  registrationSupportSummaryFields,
  type RegistrationSupportMatchState,
  type RegistrationSupportSummary,
  type SupportRequestDetail,
} from '@apuestapr/contracts';
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@apuestapr/ui';
import type { ReactElement } from 'react';

import { formatPuertoRicoInstant, puertoRicoZoneLabel } from '../format/instants.ts';

/** One label per contract field, so a widened summary stops compiling instead of rendering a raw key. */
export const registrationFieldLabels: Readonly<Record<keyof RegistrationSupportSummary, string>> = {
  legacySourceId: 'Identificador de origen',
  name: 'Nombre',
  email: 'Correo electrónico',
  phoneNumber: 'Teléfono',
  kycStatus: 'Estado de KYC',
  registeredAt: 'Registro',
  loyaltyCardNumber: 'Tarjeta de lealtad',
  whitehatUserId: 'Usuario Whitehat',
  whitehatPlayerId: 'Jugador Whitehat',
};

/** The operator-facing statement. The contract's own notice is rendered beneath it, verbatim. */
export const registrationCandidateWarning =
  'Una coincidencia es solo un candidato de soporte: no demuestra la identidad ni la titularidad de la cuenta. Verifica por un canal apropiado antes de actuar.' as const;

const matchStateSummary: Readonly<Record<RegistrationSupportMatchState, string>> = {
  none: 'Sin candidatos de registro para este correo electrónico.',
  single: 'Un candidato de registro coincide con este correo electrónico.',
  multiple: 'Varios candidatos de registro coinciden con este correo electrónico.',
};

const FieldValue = ({
  field,
  candidate,
}: {
  readonly field: keyof RegistrationSupportSummary;
  readonly candidate: RegistrationSupportSummary;
}): ReactElement => {
  const value = candidate[field];
  if (value === null) return <>No disponible</>;
  if (field === 'registeredAt') {
    return (
      <span className="grid gap-1">
        <time dateTime={value}>{formatPuertoRicoInstant(value)}</time>
        <span className="text-xs text-muted-foreground">{puertoRicoZoneLabel}</span>
      </span>
    );
  }
  return <>{value}</>;
};

const Candidate = ({
  candidate,
  position,
  total,
}: {
  readonly candidate: RegistrationSupportSummary;
  readonly position: number;
  readonly total: number;
}): ReactElement => (
  <li className="rounded-md border border-border p-3">
    <p className="m-0 mb-2 font-semibold">
      Candidato {String(position)} de {String(total)}
    </p>
    <dl className="grid gap-2 sm:grid-cols-2">
      {registrationSupportSummaryFields.map((field) => (
        <div key={field}>
          <dt className="text-sm font-semibold">{registrationFieldLabels[field]}</dt>
          <dd className="m-0">
            <FieldValue field={field} candidate={candidate} />
          </dd>
        </div>
      ))}
    </dl>
  </li>
);

export const RegistrationCandidates = ({
  detail,
}: {
  readonly detail: SupportRequestDetail;
}): ReactElement => {
  const candidates = detail.registrationMatches;

  return (
    <Card>
      <CardHeader>
        <CardTitle level={3}>Candidatos de registro</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-md border-2 border-foreground bg-muted p-3">
          <p className="m-0 font-semibold">{registrationCandidateWarning}</p>
          {/* The contract's own statement, verbatim, so what the payload says is on the page too. */}
          <p className="m-0 mt-2 text-xs text-muted-foreground">
            {detail.registrationIdentityNotice}
          </p>
        </div>

        <p className="m-0 flex flex-wrap items-center gap-2">
          <Badge
            variant={detail.registrationMatchState === 'multiple' ? 'accent' : 'neutral'}
            shape={detail.registrationMatchState === 'multiple' ? 'diamond' : 'circle'}
          >
            {String(candidates.length)}
          </Badge>
          {matchStateSummary[detail.registrationMatchState]}
        </p>

        {candidates.length === 0 ? (
          <p className="m-0 text-muted-foreground">
            No encontrar candidatos es una respuesta válida: la tabla de registros de este entorno
            es sintética y puede estar vacía.
          </p>
        ) : (
          <ol className="m-0 grid list-none gap-3 p-0">
            {candidates.map((candidate, index) => (
              <Candidate
                key={candidate.legacySourceId}
                candidate={candidate}
                position={index + 1}
                total={candidates.length}
              />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};
